/**
 * Client wrappers for MSI3 teaching callables.
 */

import firebase from "firebase/compat/app";

/**
 * @param {{ learnerType: "student"|"clinician", orgId?: string|null }} input
 */
export async function saveMsi3LearnerProfile(input) {
  const callable = firebase
    .functions()
    .httpsCallable("saveMsi3LearnerProfile");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Gate a case start against the MSI3 free tier (3 distinct EM cases).
 * @param {{ caseId: string, caseBank?: string, specialty?: string }} input
 */
export async function assertMsi3CaseStart(input) {
  const callable = firebase.functions().httpsCallable("assertMsi3CaseStart");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Learner: paid-access resolution for the renewal banner (includes expiresAt
 * for student_plan and school_seat).
 * @returns {Promise<{ allowed: boolean, reason: string, expiresAt?: number|null }>}
 */
export async function getMsi3LearnerAccess() {
  const callable = firebase.functions().httpsCallable("getMsi3LearnerAccess");
  const result = await callable({});
  return result?.data || null;
}

/**
 * Faculty/admin: draft an org-private MSI3 case from a topic.
 * @param {{ topic: string, orgId: string, specialty?: string, topicIds?: string[], resultStyle?: string, transcriptDetail?: string }} input
 * @returns {Promise<{ caseId: string, orgId: string }>}
 */
export async function draftMsi3Case(input) {
  const callable = firebase.functions().httpsCallable("draftMsi3Case", {
    timeout: 300000,
  });
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Faculty/admin: edit a draft or reviewed MSI3 case.
 * @param {{
 *   caseId: string,
 *   title: string,
 *   transcript: string,
 *   diagnoses: Array<{ name: string, mustNotMiss: boolean }>,
 *   diagnosis?: string,
 *   teachingPoints?: string[],
 *   diagnosticTests?: Array<object>,
 *   resultStyle?: string,
 *   specialty?: string,
 *   topicIds?: string[],
 * }} input
 */
export async function updateMsi3CaseDraft(input) {
  const callable = firebase.functions().httpsCallable("updateMsi3CaseDraft");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Faculty/admin: read the answer key for a case (never exposed to learners).
 * @param {{ caseId: string }} input
 * @returns {Promise<{
 *   caseId: string,
 *   diagnoses: Array<{ name: string, mustNotMiss: boolean }>,
 *   diagnosis: string,
 *   teachingPoints: string[],
 *   diagnosticTests: Array<object>,
 *   resultStyle: string,
 * }>}
 */
export async function getMsi3CaseKey(input) {
  const callable = firebase.functions().httpsCallable("getMsi3CaseKey");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Learner scoring shell. Choice options omit correct flags.
 * @param {{ sessionId: string }} input
 */
export async function getMsi3ScoringShell(input) {
  const callable = firebase.functions().httpsCallable("getMsi3ScoringShell");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Grade one Morning Report step. The server owns the try number and the points.
 * @param {{ sessionId: string, stepId: "set"|"choice"|"commit", entries?: string[], selectedIds?: string[], selectedText?: string }} input
 */
export async function gradeMsi3ScoredStep(input) {
  const callable = firebase.functions().httpsCallable("gradeMsi3ScoredStep");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Faculty/admin: clone a case + answer key into a new org-only draft.
 * @param {{ caseId: string }} input
 * @returns {Promise<{ caseId: string, orgId: string }>}
 */
export async function copyMsi3Case(input) {
  const callable = firebase.functions().httpsCallable("copyMsi3Case");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Faculty/admin: mark a case reviewed or retire it.
 * @param {{ caseId: string, status: "reviewed" | "retired" }} input
 */
export async function setMsi3CaseStatus(input) {
  const callable = firebase.functions().httpsCallable("setMsi3CaseStatus");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Author or global admin: mark a reviewed case Globally available.
 * Org admin (or the author, or a global admin) can turn it off.
 * @param {{ caseId: string, global: boolean }} input
 * @returns {Promise<{ caseId: string, visibility: "org" | "global" }>}
 */
export async function setMsi3CaseGlobal(input) {
  const callable = firebase.functions().httpsCallable("setMsi3CaseGlobal");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Global admin: tag an old-bank (clinicalHud3Cases) case with specialty/topicIds.
 * @param {{ caseId: string, specialty: string, topicIds?: string[] }} input
 */
export async function setMsi3CaseTags(input) {
  const callable = firebase.functions().httpsCallable("setMsi3CaseTags");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Admin / school org-admin: generate a faculty invite for a school org.
 * @param {{ orgId: string, durationDays?: number }} input
 * @returns {Promise<{ inviteCode: string, inviteUrl: string, orgId: string, expiresAt: string }>}
 */
export async function msi3GenerateFacultyInvite(input) {
  const callable = firebase
    .functions()
    .httpsCallable("msi3GenerateFacultyInvite");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Authenticated, non-anonymous account: claim a faculty invite by code.
 * @param {{ code: string }} input
 * @returns {Promise<{ success: boolean, orgId: string, facultyUid: string }>}
 */
export async function msi3ClaimFacultyInvite(input) {
  const callable = firebase.functions().httpsCallable("msi3ClaimFacultyInvite");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Global admin: faculty grants still waiting for approval.
 * @returns {Promise<{ grants: Array<{ uid: string, orgId: string, status: string, requestedBy: string, requestedAt: string }> }>}
 */
export async function listMsi3PendingFacultyGrants() {
  const callable = firebase
    .functions()
    .httpsCallable("listMsi3PendingFacultyGrants");
  const result = await callable({});
  return result?.data || { grants: [] };
}

/**
 * Org-admin or global admin: faculty grants for one school, plus whether
 * that org is in residencyOrgs (for residency40 purchase UI).
 * @param {{ orgId: string }} input
 * @returns {Promise<{ orgId?: string, grants: Array<object>, residencyVerified?: boolean }>}
 */
export async function listMsi3OrgFacultyGrants(input) {
  const callable = firebase.functions().httpsCallable("listMsi3OrgFacultyGrants");
  const result = await callable(input);
  return result?.data || { grants: [], residencyVerified: false };
}

/**
 * Global admin: approve or reject a faculty grant.
 * @param {{ uid: string, decision: "approved" | "rejected" }} input
 */
export async function decideMsi3FacultyGrant(input) {
  const callable = firebase.functions().httpsCallable("decideMsi3FacultyGrant");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Admin / school org-admin: add/remove a learner seat or set the seat tier.
 * @param {{
 *   orgId: string,
 *   action: "add" | "remove" | "setTier",
 *   uid?: string,
 *   tier?: string | number,
 *   expiresAt?: number | string,
 * }} input
 */
export async function assignMsi3OrgSeat(input) {
  const callable = firebase.functions().httpsCallable("assignMsi3OrgSeat");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Learner: report a completed AcuityDrill (clinicalHud5) case attempt.
 * The callable enforces that report.uid matches the authenticated caller.
 * @param {{ uid: string, appId: string, caseId: string, overallScore: number, completedAt?: number|string }} input
 * @returns {Promise<{ attemptId: string, caseId: string, overallScore: number }>}
 */
export async function reportMsi3Attempt(input) {
  const callable = firebase.functions().httpsCallable("reportMsi3Attempt");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Global admin: read the MSI3 access config (signup policy, hospital domains,
 * discounts, residency orgs). Student domains live in msi3StudentDomains.
 * @returns {Promise<{ signupPolicy: string, hospitalDomains: string[], domainDiscounts: Record<string, number> }>}
 */
export async function getMsi3AccessConfig() {
  const callable = firebase.functions().httpsCallable("getMsi3AccessConfig");
  const result = await callable({});
  return result?.data || null;
}

/**
 * Global admin: save the MSI3 access config.
 * @param {{
 *   signupPolicy: "allowlist" | "open",
 *   hospitalDomains?: string[],
 *   domainDiscounts?: Record<string, number>,
 *   residencyOrgs?: Record<string, { note?: string }>,
 * }} input
 */
export async function setMsi3AccessConfig(input) {
  const callable = firebase.functions().httpsCallable("setMsi3AccessConfig");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Global admin: search student-price domains (prefix on domain id).
 * @param {{ query: string, limit?: number }} input
 * @returns {Promise<{ domains: Array<object>, query: string }>}
 */
export async function listMsi3StudentDomains(input) {
  const callable = firebase.functions().httpsCallable("listMsi3StudentDomains");
  const result = await callable(input);
  return result?.data || { domains: [], query: "" };
}

/**
 * Global admin: add a manual student-price domain.
 * @param {{ domain: string, note?: string, schoolName?: string, country?: string }} input
 */
export async function addMsi3StudentDomain(input) {
  const callable = firebase.functions().httpsCallable("addMsi3StudentDomain");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Global admin: remove a student-price domain.
 * @param {{ domain: string }} input
 */
export async function removeMsi3StudentDomain(input) {
  const callable = firebase.functions().httpsCallable("removeMsi3StudentDomain");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Learner / school: start a Stripe Checkout flow for an MSI3 plan.
 * @param {{ planKey: string, orgId?: string, successUrl?: string, cancelUrl?: string }} input
 * @returns {Promise<{
 *   url: string,
 *   sessionId: string,
 *   planKey: string,
 *   mode: string,
 *   appliedDiscountPercent: number,
 *   discountSkippedReason?: string|null,
 * }>}
 */
export async function createMsi3CheckoutSession(input) {
  const callable = firebase
    .functions()
    .httpsCallable("createMsi3CheckoutSession");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Clinician: open Stripe Customer Portal for the caller's own subscription.
 * @param {{ returnUrl?: string }} [input]
 * @returns {Promise<{ url: string }>}
 */
export async function createMsi3BillingPortalSession(input = {}) {
  const callable = firebase
    .functions()
    .httpsCallable("createMsi3BillingPortalSession");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Read one learner's MSI3 attempts (self, admin, or org faculty/org-admin).
 * @param {{ uid?: string }} [input]
 * @returns {Promise<{ uid: string, attempts: Array<object> }>}
 */
export async function getMsi3Progress(input = {}) {
  const callable = firebase.functions().httpsCallable("getMsi3Progress");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Faculty / org-admin / admin: read every learner's attempts for one org plus
 * the org's EM curriculum inputs (client computes topic mastery).
 * @param {{ orgId: string }} input
 * @returns {Promise<{ orgId: string, learners: Array<object>, msi3EmCases: Array<object>, oldBankEmCases: Array<object> }>}
 */
export async function getMsi3OrgProgress(input) {
  const callable = firebase.functions().httpsCallable("getMsi3OrgProgress");
  const result = await callable(input);
  return result?.data || null;
}
